import { fromUnixTime } from 'date-fns';
import { proxy } from 'valtio';
import { z } from 'zod';
import { AuctionTypeEnum } from '@soundxyz/utils/constants/enums';
import { getDateTimeErrors } from '@components/Dashboard/Upload/helpers';
import { PersistenceStorage } from '@store/persistence';
import { PRESALE_MIN_SUPPLY } from '@utils/constants';
import { hasMoreThanXDecimals, MAX_UPLOAD_RELEASE_PRICE_DECIMALS } from '@utils/helpers';
import {
  AuctionConfigurationMintQuantityLimits,
  AuctionConfigurationPriceLimits,
  initialAuctionConfigurationMintQuantityLimits,
  initialAuctionConfigurationPriceLimits,
} from '../../fields';
import {
  decimalsError,
  EditAuctionConfigurationErrors,
  EditErrorMessages,
  initialEditAuctionConfigurationErrors,
} from '../errors';
import { AllowlistsSchema } from './allowlist';

const SaleScheduleUpdate = z.object({
  editionAddress: z.string(),
  minterAddress: z.string(),
  mintId: z.union([z.number(), z.bigint().transform(value => Number(value))]),
  price: z.string(),
  startTimeSeconds: z.number(),
  maxMintable: z.number(),
  maxMintablePerAccount: z.number(),
});

export const RangeEditionSaleScheduleUpdate = SaleScheduleUpdate.extend({
  mintType: z.literal('RangeEdition'),
  cutoffTimeSeconds: z.number(),
});

export type RangeEditionSaleScheduleUpdate = (typeof RangeEditionSaleScheduleUpdate)['_input'];

export const MerkleDropSaleScheduleUpdate = SaleScheduleUpdate.extend({
  mintType: z.literal('MerkleDrop'),
  endTimeSeconds: z.number(),
});

export type MerkleDropSaleScheduleUpdate = (typeof MerkleDropSaleScheduleUpdate)['_input'];

const LocalEditedSaleScheduleToUpdate = z.union([
  RangeEditionSaleScheduleUpdate,
  MerkleDropSaleScheduleUpdate,
]);

export const EditedAuctionConfigFields = z.object({
  editedSaleSchedules: z
    .record(z.string().nonempty(), z.array(LocalEditedSaleScheduleToUpdate))
    .superRefine((value, ctx) => {
      for (const schedules of Object.values(value)) {
        const prices: Record<'presalePricePerMint' | 'publicPricePerMint', number | undefined> = {
          presalePricePerMint: undefined,
          publicPricePerMint: undefined,
        };

        const startTimes: Record<'presaleStartTime' | 'publicStartTime', Date | undefined> = {
          presaleStartTime: undefined,
          publicStartTime: undefined,
        };

        const endTimes: Record<'presaleEndTime' | 'publicEndTime', Date | undefined> = {
          presaleEndTime: undefined,
          publicEndTime: undefined,
        };

        let maxMintable: number | undefined = undefined;

        // Set presale (merkle edition) and public (range edition) values
        for (const schedule of schedules) {
          const price = schedule.price;
          const startTime = schedule.startTimeSeconds;
          const endTime =
            schedule.mintType === 'RangeEdition'
              ? schedule.cutoffTimeSeconds
              : schedule.endTimeSeconds;
          const isPresale = schedule.mintType === 'MerkleDrop';

          if (isPresale) {
            if (price) {
              prices.presalePricePerMint = Number(price);
            }

            if (startTime) {
              startTimes.presaleStartTime = fromUnixTime(startTime);
            }

            if (endTime) {
              endTimes.presaleEndTime = fromUnixTime(endTime);
            }
          } else {
            if (price) {
              prices.publicPricePerMint = Number(price);
            }

            if (startTime) {
              startTimes.publicStartTime = fromUnixTime(startTime);
            }

            if (endTime) {
              endTimes.publicEndTime = fromUnixTime(endTime);
            }

            if (schedule.maxMintable) maxMintable = schedule.maxMintable;
          }
        }

        // Validate presale (merkle edition) and public (range edition) values
        for (const schedule of schedules) {
          const isPresale = schedule.mintType === 'MerkleDrop';
          const price = schedule.price;
          const startTime = schedule.startTimeSeconds;
          const endTime =
            schedule.mintType === 'RangeEdition'
              ? schedule.cutoffTimeSeconds
              : schedule.endTimeSeconds;

          const pricePath = isPresale ? 'presalePricePerMint' : 'publicPricePerMint';

          const timingErrors = getDateTimeErrors({
            startTime,
            endTime,
            type: isPresale ? 'presale' : 'public',
          });

          // Price validation
          if (!price || price === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: isPresale
                ? EditErrorMessages.presalePricePerMint.required_error
                : EditErrorMessages.publicPricePerMint.required_error,
              path: [pricePath],
              fatal: true,
            });
            return z.NEVER;
          }

          if (timingErrors.public) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: timingErrors.public.error,
              path: [timingErrors.public.path],
              fatal: true,
            });
          }

          if (timingErrors.presale && isPresale) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: timingErrors.presale.error,
              path: [timingErrors.presale.path],
              fatal: true,
            });
          }

          if (
            startTimes.presaleStartTime &&
            startTimes.publicStartTime &&
            startTimes.presaleStartTime > startTimes.publicStartTime
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Public sale must be at the same time or after presale',
              path: ['publicStartTime'],
            });
          }

          if (
            price &&
            hasMoreThanXDecimals({
              value: parseFloat(price.toString()),
              maxDecimals: MAX_UPLOAD_RELEASE_PRICE_DECIMALS,
            })
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: decimalsError,
              path: [pricePath],
              fatal: true,
            });
            return z.NEVER;
          }

          if (
            typeof prices.presalePricePerMint === 'number' &&
            typeof prices.publicPricePerMint === 'number' &&
            prices.presalePricePerMint > prices.publicPricePerMint
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must be more than presale mint price',
              path: ['publicPricePerMint'],
            });
          }

          // Public price must be within price limits
          const publicMaxPrice = editedAuctionConfigState.priceLimit.publicMaxPrice;
          const publicMinPrice = editedAuctionConfigState.priceLimit.publicMinPrice;

          const publicPriceRangeError =
            publicMinPrice === publicMaxPrice
              ? `Value must be ${publicMinPrice}`
              : `Value must be between ${publicMinPrice} and ${publicMaxPrice}`;

          if (
            publicMaxPrice != null &&
            publicMinPrice != null &&
            typeof prices.publicPricePerMint === 'number'
          )
            if (
              prices.publicPricePerMint < publicMinPrice ||
              prices.publicPricePerMint > publicMaxPrice
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: publicPriceRangeError,
                path: ['publicPricePerMint'],
              });
            }

          // Presale price must be within price limits
          const presaleMaxPrice = editedAuctionConfigState.priceLimit.presaleMaxPrice;
          const presaleMinPrice = editedAuctionConfigState.priceLimit.presaleMinPrice;

          const presalePriceRangeError =
            presaleMinPrice === presaleMaxPrice
              ? `Value must be ${presaleMinPrice}`
              : `Value must be between ${presaleMinPrice} and ${presaleMaxPrice}`;

          if (
            presaleMaxPrice != null &&
            presaleMinPrice != null &&
            typeof prices.presalePricePerMint === 'number'
          )
            if (
              prices.presalePricePerMint < presaleMinPrice ||
              prices.presalePricePerMint > presaleMaxPrice
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: presalePriceRangeError,
                path: ['presalePricePerMint'],
              });
            }
        }

        for (const schedule of schedules) {
          const isMerkle = schedule.mintType === 'MerkleDrop';

          const scheduleMaxMintable = schedule.maxMintable;

          if (scheduleMaxMintable != null) {
            // Total mint supply must be within min and max quantity limits
            const minQuantity = isMerkle
              ? PRESALE_MIN_SUPPLY
              : editedAuctionConfigState.mintQuantityLimit.minQuantity;
            const maxQuantity = isMerkle
              ? maxMintable
              : editedAuctionConfigState.mintQuantityLimit.maxQuantity;

            const totalMintSupplyError =
              (minQuantity && Number(scheduleMaxMintable) < minQuantity) ||
              (maxQuantity && Number(scheduleMaxMintable) > maxQuantity)
                ? `Value must be between ${minQuantity} and ${maxQuantity}`
                : null;

            if (totalMintSupplyError) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: totalMintSupplyError,
                path: [isMerkle ? 'presaleMaxSupply' : 'maxMintable'],
              });
              return z.NEVER;
            }
          }

          const maxMintablePerAccount = schedule.maxMintablePerAccount;

          if (maxMintablePerAccount != null) {
            // Total mint supply must be within min and max quantity limits
            const minPublicSaleMaxMintsPerWallet =
              editedAuctionConfigState.mintQuantityLimit[
                isMerkle ? 'minPresaleMaxMintsPerWallet' : 'minPublicSaleMaxMintsPerWallet'
              ];
            const maxPublicSaleMaxMintsPerWallet =
              editedAuctionConfigState.mintQuantityLimit[
                isMerkle ? 'maxPresaleMaxMintsPerWallet' : 'maxPublicSaleMaxMintsPerWallet'
              ];

            const mintsPerWalletError =
              (minPublicSaleMaxMintsPerWallet &&
                Number(maxMintablePerAccount) < minPublicSaleMaxMintsPerWallet) ||
              (maxPublicSaleMaxMintsPerWallet &&
                Number(maxMintablePerAccount) > maxPublicSaleMaxMintsPerWallet)
                ? `Value must be between ${minPublicSaleMaxMintsPerWallet} and ${maxPublicSaleMaxMintsPerWallet}`
                : null;

            if (mintsPerWalletError) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: mintsPerWalletError,
                path: [isMerkle ? 'presaleMaxMintsPerWallet' : 'publicMaxMintsPerWallet'],
              });
            }
          }
        }
      }

      return;
    }),
  allowlists: AllowlistsSchema,
});

export type EditedAuctionConfigFields = (typeof EditedAuctionConfigFields)['_input'];

export type EditedSaleScheduleToUpdate = (typeof LocalEditedSaleScheduleToUpdate)['_input'];

const initialEditedAuctionConfigurationFields = () =>
  ({
    editedSaleSchedules: {},
    allowlists: {},
  }) satisfies EditedAuctionConfigFields;

export const EditedAuctionConfigSchema = z.object({
  fields: EditedAuctionConfigFields,
  errors: EditAuctionConfigurationErrors,
  isValid: z.boolean(),
  priceLimit: AuctionConfigurationPriceLimits,
  mintQuantityLimit: AuctionConfigurationMintQuantityLimits,
  hasPresaleMint: z.boolean(),
  auctionType: z.nativeEnum(AuctionTypeEnum).nullable(),
});

export const EditedAuctionConfigPersistence = PersistenceStorage({
  schema: EditedAuctionConfigFields,
  key: '@soundxyz/editedAuctionConfig',
});

export type EditedAuctionConfigState = (typeof EditedAuctionConfigSchema)['_input'];

export const editedAuctionConfigState: EditedAuctionConfigState = proxy({
  fields: initialEditedAuctionConfigurationFields(),
  errors: initialEditAuctionConfigurationErrors(),
  isValid: false,
  priceLimit: initialAuctionConfigurationPriceLimits(),
  mintQuantityLimit: initialAuctionConfigurationMintQuantityLimits(),
  hasPresaleMint: false,
  auctionType: null,
});
